import { useEffect, useState } from 'react';
import { FileIcon } from '../card/FileIcon';
import { useQuery } from '@tanstack/react-query';
import { Download, FileText, FileType2, LayoutGrid, List, MessageSquare, Search } from 'lucide-react';
import { MEDIA_KIND_TEXT, MEDIA_SORT_TEXT, type MediaItem, type MediaKind, type MediaSort } from '@kaba/shared';
import { api } from '../../lib/api';
import { accountPref, deviceClass, setAccountPref } from '../../lib/boardNav';
import { AttachmentPreview } from '../card/AttachmentPreview';
import { Button, cx } from '../ui/ui';

const PAGE = 60;
const sizeText = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toLocaleString('de-DE', { maximumFractionDigits: 1 })} MB`);
const dateText = (iso: string) => new Date(iso).toLocaleDateString('de-DE', { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Dateien eines Boards (Z02): Filter nach Art, Suche nach Dateiname und Karte, Sortierung nach Datum.
 * Tippen öffnet die bestehende Vorschau; der Download bleibt eine eigene Aktion.
 */
export function MediaView({ boardId, onOpenCard }: { boardId: string; onOpenCard: (id: string) => void }) {
  const [kind, setKind] = useState<MediaKind>('all');
  const [sort, setSort] = useState<MediaSort>('new');
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [archived, setArchived] = useState(false);
  const [layout, setLayout] = useState<'list' | 'grid'>(() => accountPref('mediaLayout', deviceClass() === 'phone' ? 'list' : 'grid'));
  const [pages, setPages] = useState(1);
  const [preview, setPreview] = useState<number | null>(null);
  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => setPages(1), [kind, sort, term, archived]);
  useEffect(() => setAccountPref('mediaLayout', layout), [layout]);

  const params = (offset: number) => new URLSearchParams({ type: kind, sort, q: term, offset: String(offset), limit: String(PAGE), ...(archived ? { archived: '1' } : {}) });
  const data = useQuery({
    queryKey: ['media', boardId, kind, sort, term, archived, pages],
    queryFn: async () => {
      const all: MediaItem[] = [];
      let total = 0;
      for (let p = 0; p < pages; p++) {
        const r = await api.get<{ total: number; items: MediaItem[] }>(`/api/boards/${boardId}/media?${params(p * PAGE)}`);
        total = r.total;
        all.push(...r.items);
        if (r.items.length < PAGE) break;
      }
      return { total, items: all };
    },
    placeholderData: (prev) => prev,
  });
  const items = data.data?.items ?? [];
  const total = data.data?.total ?? 0;

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 px-3 pt-2 pb-10 sm:px-4">
      <div className="flex flex-col gap-2 rounded-xl border border-[var(--line)] bg-[var(--panel)] p-2.5">
        <div role="group" aria-label="Art der Datei" className="flex flex-wrap gap-1">
          {(Object.keys(MEDIA_KIND_TEXT) as MediaKind[]).map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={kind === k}
              onClick={() => setKind(k)}
              className={cx('min-h-11 rounded-lg px-3 text-sm sm:min-h-9', kind === k ? 'bg-accent-soft font-semibold' : 'hover:bg-black/5 dark:hover:bg-white/10')}
            >
              {MEDIA_KIND_TEXT[k]}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex min-w-0 flex-1 basis-56 items-center gap-2 rounded-lg border border-[var(--line)] bg-[var(--surface)] px-2">
            <Search size={16} className="shrink-0 text-[var(--ink-soft)]" aria-hidden />
            <input
              type="search"
              aria-label="Dateiname oder Karte suchen"
              placeholder="Dateiname oder Karte …"
              className="min-h-11 min-w-0 flex-1 bg-transparent text-base outline-none sm:min-h-9 sm:text-sm"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </label>
          <select aria-label="Sortierung" className="min-h-11 rounded-lg border border-[var(--line)] bg-[var(--panel)] px-2 text-sm sm:min-h-9" value={sort} onChange={(e) => setSort(e.target.value as MediaSort)}>
            {(Object.keys(MEDIA_SORT_TEXT) as MediaSort[]).map((s) => (
              <option key={s} value={s}>
                {MEDIA_SORT_TEXT[s]}
              </option>
            ))}
          </select>
          <label className="flex min-h-11 items-center gap-2 text-sm sm:min-h-9">
            <input type="checkbox" className="size-5 accent-[var(--color-accent)]" checked={archived} onChange={(e) => setArchived(e.target.checked)} />
            Archivierte Karten
          </label>
          <div role="group" aria-label="Darstellung" className="ml-auto flex gap-1">
            <button type="button" aria-pressed={layout === 'list'} aria-label="Als Liste" onClick={() => setLayout('list')} className={cx('inline-flex size-11 items-center justify-center rounded-lg sm:size-9', layout === 'list' ? 'bg-accent-soft' : 'hover:bg-black/5 dark:hover:bg-white/10')}>
              <List size={17} aria-hidden />
            </button>
            <button type="button" aria-pressed={layout === 'grid'} aria-label="Als Galerie" onClick={() => setLayout('grid')} className={cx('inline-flex size-11 items-center justify-center rounded-lg sm:size-9', layout === 'grid' ? 'bg-accent-soft' : 'hover:bg-black/5 dark:hover:bg-white/10')}>
              <LayoutGrid size={17} aria-hidden />
            </button>
          </div>
        </div>
      </div>

      <p className="px-1 text-sm text-[var(--ink-soft)]" role="status" aria-live="polite">
        {data.isError ? 'Die Dateien konnten nicht geladen werden.' : data.isLoading ? 'Lädt …' : total === 1 ? '1 Datei' : `${total} Dateien`}
      </p>
      {!data.isLoading && !data.isError && !items.length && (
        <div className="rounded-2xl border-2 border-dashed border-[var(--line)] p-8 text-center text-sm text-[var(--ink-soft)]">{term || kind !== 'all' ? 'Keine passenden Dateien.' : 'In diesem Board gibt es noch keine Dateien.'}</div>
      )}

      {layout === 'grid' ? (
        <ul className="grid grid-cols-2 gap-3 min-[480px]:grid-cols-3 md:grid-cols-4 lg:grid-cols-6" aria-label="Dateien">
          {items.map((it, i) => (
            <li key={it.id} className="flex min-w-0 flex-col overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--panel)]">
              <button type="button" onClick={() => setPreview(i)} className="block aspect-[4/3] w-full bg-[var(--column)]" aria-label={`Vorschau: ${it.filename}`}>
                {it.isImage && it.thumbUrl ? (
                  <img src={it.thumbUrl} alt="" loading="lazy" decoding="async" className="size-full object-cover" />
                ) : (
                  <span className="flex size-full items-center justify-center text-[var(--ink-soft)]"><FileIcon mime={it.mime} filename={it.filename} size={32} /></span>
                )}
              </button>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5 p-2">
                <span className="truncate text-sm font-medium" title={it.filename}>
                  {it.filename}
                </span>
                <button type="button" className="truncate text-left text-xs text-accent-fg hover:underline" onClick={() => onOpenCard(it.cardId)}>
                  {it.cardTitle}
                </button>
                <span className="flex items-center gap-1 text-xs text-[var(--ink-soft)]">
                  {it.commentId && <MessageSquare size={12} aria-label="aus einem Kommentar" />}
                  {dateText(it.createdAt)}
                  <a href={`/api/files/${it.id}/download`} className="ml-auto inline-flex size-9 items-center justify-center rounded-lg hover:bg-black/5 dark:hover:bg-white/10" aria-label={`${it.filename} herunterladen`}>
                    <Download size={15} aria-hidden />
                  </a>
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="flex flex-col divide-y divide-[var(--line)] overflow-hidden rounded-xl border border-[var(--line)] bg-[var(--panel)]" aria-label="Dateien">
          {items.map((it, i) => (
            <li key={it.id} className="flex items-center gap-3 p-2">
              <button type="button" onClick={() => setPreview(i)} className="flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[var(--column)] text-[var(--ink-soft)]" aria-label={`Vorschau: ${it.filename}`}>
                {it.isImage && it.thumbUrl ? <img src={it.thumbUrl} alt="" loading="lazy" decoding="async" className="size-full object-cover" /> : <FileIcon mime={it.mime} filename={it.filename} size={24} />}
              </button>
              <div className="min-w-0 flex-1">
                <button type="button" onClick={() => setPreview(i)} className="block max-w-full truncate text-left text-sm font-medium hover:underline">
                  {it.filename}
                </button>
                <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-[var(--ink-soft)]">
                  <button type="button" className="max-w-full truncate text-accent-fg hover:underline" onClick={() => onOpenCard(it.cardId)}>
                    {it.cardTitle}
                  </button>
                  {it.cardArchived && <span>(archiviert)</span>}
                  {it.commentId && (
                    <span className="inline-flex items-center gap-1">
                      <MessageSquare size={12} aria-hidden /> aus Kommentar
                    </span>
                  )}
                  <span>· {dateText(it.createdAt)}</span>
                  <span>· {sizeText(it.size)}</span>
                  {it.uploaderName && <span>· {it.uploaderName}</span>}
                </p>
              </div>
              <a href={`/api/files/${it.id}/download`} className="inline-flex size-11 shrink-0 items-center justify-center rounded-lg text-[var(--ink-soft)] hover:bg-black/5 sm:size-9 dark:hover:bg-white/10" aria-label={`${it.filename} herunterladen`}>
                <Download size={17} aria-hidden />
              </a>
            </li>
          ))}
        </ul>
      )}
      {items.length < total && (
        <Button className="self-center" disabled={data.isFetching} onClick={() => setPages((p) => p + 1)}>
          Weitere laden ({total - items.length})
        </Button>
      )}
      <AttachmentPreview items={items} index={preview} onIndex={setPreview} onClose={() => setPreview(null)} />
    </div>
  );
}
